import { auth, init, subscription } from 'cubeforall';
import type {
  CubeActiveAccount,
  CubeSdkConfig,
  CubeSubscriptionResult,
  CubeSsoLoginPayload,
  CubeSsoLoginResponse,
} from '@/types/cube-sso';
import { apiUrl } from '@constants/url';
import axiosInstance from '@libs/axios';

let initPromise: Promise<void> | null = null;

export const CUBE_AUTH_SOURCE = 'cubeforall';
export const CUBE_EXTERNAL_PROVIDER = 'ccn';

const CUBE_POLICY_SIGNUP_SIGNIN = 'B2C_1A_SIGNUP_SIGNINNEWUSERSYN';
const CUBE_POLICY_FORGOT_PASSWORD = 'b2c_1a_passwordreset';

const DEFAULT_AUTHORITY_URLS = {
  sandbox: 'https://ccnssoppd.b2clogin.com/ccnssoppd.onmicrosoft.com',
  prod: 'https://ccnsso.b2clogin.com/ccnsso.onmicrosoft.com',
} as const;

const normalizeCubeEnvironment = (value?: string) => {
  const normalized = value?.toLowerCase();

  if (normalized === 'sandbox' || normalized === 'ppd') {
    return 'sandbox';
  }

  return 'prod';
};

const normalizeLoginType = (value?: string) => {
  const normalized = value?.toLowerCase();

  if (normalized === 'redirect' || normalized === 'silent' || normalized === 'popup') {
    return normalized;
  }

  return 'popup';
};

export const getCubeConfig = (): CubeSdkConfig => {
  const environment = normalizeCubeEnvironment(import.meta.env.VITE_CUBE_ENVIRONMENT);

  return {
    serviceId: import.meta.env.VITE_CUBE_SERVICE_ID,
    clientId: import.meta.env.VITE_CUBE_CLIENT_ID,
    environment,
    loginType: normalizeLoginType(import.meta.env.VITE_CUBE_LOGIN_TYPE),
    authorityUrl: import.meta.env.VITE_CUBE_AUTHORITY_URL || DEFAULT_AUTHORITY_URLS[environment],
    signupSigninPolicy: import.meta.env.VITE_CUBE_SIGNUP_SIGNIN_POLICY || CUBE_POLICY_SIGNUP_SIGNIN,
    forgotPasswordPolicy: import.meta.env.VITE_CUBE_FORGOT_PASSWORD_POLICY || CUBE_POLICY_FORGOT_PASSWORD,
    pollIntervalMilliseconds: import.meta.env.VITE_CUBE_POLL_INTERVAL_MS || '0',
    redirectUrl: import.meta.env.VITE_CUBE_REDIRECT_URL || window.location.origin,
  };
};

function assertCubeConfig(
  config: CubeSdkConfig
): asserts config is CubeSdkConfig & Required<Pick<CubeSdkConfig, 'serviceId' | 'clientId'>> {
  const missingKeys = ['serviceId', 'clientId'].filter((key) => !config[key as keyof CubeSdkConfig]);

  if (missingKeys.length > 0) {
    throw new Error(`CUBE SSO configuration is incomplete: ${missingKeys.join(', ')}`);
  }
}

export const initializeCubeSdk = async (callbacks?: {
  onSuccess?: (result: unknown) => void;
  onError?: (error: unknown) => void;
}) => {
  if (typeof window === 'undefined') {
    throw new Error('CUBE SSO is only available in the browser');
  }

  const config = getCubeConfig();
  assertCubeConfig(config);

  if (!initPromise) {
    initPromise = Promise.resolve(
      init(
        config.serviceId,
        config.clientId,
        config.environment,
        config.loginType,
        config.authorityUrl,
        config.signupSigninPolicy,
        config.forgotPasswordPolicy,
        config.pollIntervalMilliseconds,
        config.redirectUrl,
        callbacks?.onSuccess,
        callbacks?.onError
      )
    ).then(() => undefined);
  }

  return initPromise;
};

export const triggerCubeLogin = async () => {
  await initializeCubeSdk();
  return auth.signIn();
};

export const getCubeIdentityBundle = async () => {
  await initializeCubeSdk();
  const activeAccount = (auth.getActiveAccount?.() as CubeActiveAccount | null | undefined) ?? null;
  const subscriptionResult =
    ((await subscription.checkSubscription()) as CubeSubscriptionResult | null | undefined) ?? null;

  return {
    activeAccount,
    subscription: subscriptionResult,
  };
};

export const guardCubeSession = async () => {
  await initializeCubeSdk();

  try {
    await auth.guard();
    return true;
  } catch {
    return false;
  }
};

export const exchangeCubeSsoLogin = async (payload: CubeSsoLoginPayload) => {
  const response = await axiosInstance.post<CubeSsoLoginResponse>(apiUrl.cubeSso.login, payload);
  return response.data;
};
